import { z } from 'zod';

import {
  APP_BUILDER_SESSION_PATH_PREFIX,
  Client as ClientType,
  CLIENT_APPS_PAGE,
  Company as CompanyType,
  ExtensionConfig,
  URLValidationRegex,
} from 'src/constants';
import { EmbedErrors } from 'src/constants/errorConsts/errorCodesConsts';
import { InitialConnectAppFormValues } from 'src/legacy/components/ExtensionPage/ConnectManualApp';
import type { SettingsPageId } from 'src/routes/appSetup/builder/settings/settingsPages';
import {
  CustomFieldOption,
  isMultiSelectCustomField,
  isSelectCustomField,
} from 'src/store/clients/types';
import { ExtensionItem, ExtensionItemFields } from 'src/store/dashboard/types';
import UrlUtils from 'src/utils/UrlUtils';

/** Host suffix every app-builder deployment (production alias and preview) sits on. */
const VERCEL_APP_DOMAIN = '.vercel.app';

/**
 * Deployment-host suffix for the Assembly Apps Vercel team, which every generated
 * app is deployed under. Preview hosts look like
 * `app-<id>-<hash>-assembly-apps.vercel.app`.
 */
const ASSEMBLY_APPS_VERCEL_SUFFIX = '-assembly-apps.vercel.app';

/** The same team marker as it appears in a bare host label (no domain). */
const ASSEMBLY_APPS_LABEL_SUFFIX = '-assembly-apps';

/**
 * An app is a "draft" only when it was created by the app builder (has an
 * appBuilderId) and has not been published yet. Keep this the single source of
 * truth for the Draft badge so the sidebar and the App Library can't drift.
 */
export const isDraftApp = (config?: ExtensionConfig): boolean =>
  Boolean(config?.isDraft && config?.appBuilderId);

/**
 * Builds the builder-session route for an app-builder app. Omit `tab` so the
 * session restores the saved editor/live preference (and then writes `?tab=`
 * once it knows). Pass `tab` (or `editor: true`) only to pin a specific tab,
 * and `settings` to open that settings page.
 */
export const getBuilderSessionPath = (
  builderId: string,
  {
    editor,
    tab,
    settings,
  }: {
    editor?: boolean;
    tab?: 'editor' | 'live' | 'qa';
    settings?: SettingsPageId;
  } = {},
): string => {
  const sessionTab = tab ?? (editor ? 'editor' : undefined);
  const search = new URLSearchParams();
  if (sessionTab) search.set('tab', sessionTab);
  if (settings) search.set('settings', settings);
  const query = search.toString();
  return `${APP_BUILDER_SESSION_PATH_PREFIX}${builderId}${query ? `?${query}` : ''}`;
};

/**
 * The builder session with settings open on `page`. Pins the editor tab:
 * settings replaces the split view's preview, so a session restoring to
 * live would otherwise land without it.
 */
export const getBuilderSettingsPath = (
  builderId: string,
  page: SettingsPageId = 'overview',
): string =>
  getBuilderSessionPath(builderId, { tab: 'editor', settings: page });

/** The canonical app route: a published app's live mode is the app itself. */
export const getAppRoutePath = (installId: string): string =>
  `/apps/${installId}`;

/**
 * Where the App Library opens a builder app: a draft in the editor, a
 * published app at its own route rather than inside the builder chrome.
 */
export const getAppLibraryBuilderPath = (
  builderId: string,
  installId: string,
  isDraft: boolean,
): string =>
  isDraft
    ? getBuilderSessionPath(builderId, { tab: 'editor' })
    : getAppRoutePath(installId);

/** The app builder that installed each sidebar entry, by entry (module) id. */
export const getBuilderIdByModuleId = (
  appBuilders: { id: string; installedModuleId?: string }[] | undefined,
): Map<string, string> => {
  const map = new Map<string, string>();
  appBuilders?.forEach((builder) => {
    if (builder.installedModuleId) {
      map.set(builder.installedModuleId, builder.id);
    }
  });
  return map;
};

/** How many sidebar entries share each appId, so multi-entry apps stand out. */
export const getEntryCountByAppId = (
  extensionsSettings: Record<string, ExtensionConfig> | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  Object.values(extensionsSettings ?? {}).forEach((config) => {
    if (config.appId) {
      map.set(config.appId, (map.get(config.appId) ?? 0) + 1);
    }
  });
  return map;
};

// Resolves which builder, if any, a sidebar entry belongs to. Single-entry
// apps use the install-scoped appBuilderId directly (it's unambiguous and
// avoids a first-paint flash while the app-builders query resolves). Multi-
// entry apps must go through builderIdByModuleId so only the builder's own
// installed entry resolves to a builderId — every sibling carries the same
// appBuilderId and would otherwise all route into the builder.
export const resolveBuilderIdForEntry = (
  extensionId: string,
  extensionConfig: ExtensionConfig,
  builderIdByModuleId: Map<string, string>,
  entryCountByAppId: Map<string, number>,
): string | undefined => {
  const isSoleEntry =
    !extensionConfig.appId ||
    (entryCountByAppId.get(extensionConfig.appId) ?? 0) <= 1;
  return (
    (isSoleEntry ? extensionConfig.appBuilderId : undefined) ??
    builderIdByModuleId.get(extensionId)
  );
};

/**
 * Resolves a custom field value, handling multiSelect fields by converting option IDs to labels
 */
const CHECKBOX_CHECKED_LABEL = 'Yes';
const CHECKBOX_UNCHECKED_LABEL = 'No';

export const resolveCustomFieldValue = (
  rawValue: unknown,
  customFields?: Record<string, CustomFieldOption>,
  fieldKey?: string,
): string => {
  const customField =
    customFields && fieldKey ? customFields[fieldKey] : undefined;

  // An unchecked checkbox is stored as false (or not at all), which would
  // otherwise be dropped as empty.
  if (customField?.type === 'checkbox') {
    return rawValue === true
      ? CHECKBOX_CHECKED_LABEL
      : CHECKBOX_UNCHECKED_LABEL;
  }

  if (!rawValue) {
    return '';
  }

  if (customField) {
    // Option ids are internal; embedded apps get the labels.
    if (
      isMultiSelectCustomField(customField) ||
      isSelectCustomField(customField)
    ) {
      const optionIds = Array.isArray(rawValue) ? rawValue : [rawValue];
      const labels = optionIds
        .map((optionId) => {
          const option = customField.options?.find(
            (opt) => opt.id === optionId,
          );
          return option?.label;
        })
        .filter((label): label is string => !!label);

      return labels.join(',');
    }
  }

  // Dates pass through as the stored YYYY-MM-DD string.
  return typeof rawValue === 'string' ? rawValue : '';
};

/**
 * Some marketplace app URLs already carry a query string. Concatenating
 * `?token=` then hides the token from URLSearchParams, so identity guards
 * remount the iframe on every reissue. Rewrite that glued marker to `&token=`.
 */
export const coerceEmbedTokenQuery = (embedCode: string): string => {
  const marker = '?token=';
  const idx = embedCode.lastIndexOf(marker);
  if (idx <= 0) return embedCode;
  if (embedCode.slice(0, idx).includes('?')) {
    return `${embedCode.slice(0, idx)}&${embedCode.slice(idx + 1)}`;
  }
  return embedCode;
};

const parseEmbedUrl = (embedCode: string): URL | null => {
  try {
    return new URL(coerceEmbedTokenQuery(embedCode));
  } catch {
    return null;
  }
};

/**
 * Strips the `token` query param from an embed URL while preserving all other
 * query params. Returns null for missing or invalid input.
 */
export const getUrlWithoutToken = (embedCode?: string): string | null => {
  if (!embedCode) return null;
  const url = parseEmbedUrl(embedCode);
  if (!url) return null;
  url.searchParams.delete('token');
  return url.toString();
};

export const extractTokenFromEmbedUrl = (embedCode?: string): string | null => {
  if (!embedCode) return null;
  return parseEmbedUrl(embedCode)?.searchParams.get('token') ?? null;
};

export const replaceTokenInEmbedUrl = (
  embedCode: string,
  newToken: string,
): string => {
  const url = parseEmbedUrl(embedCode);
  if (!url) return embedCode;
  url.searchParams.set('token', newToken);
  return url.toString();
};

export const appendTokenToEmbedUrl = (
  baseUrl: string,
  token: string,
): string => {
  try {
    const url = new URL(baseUrl);
    url.searchParams.set('token', token);
    return url.toString();
  } catch {
    return '';
  }
};

/**
 * Swaps an embed URL's origin for a preview deployment of the SAME app, keeping
 * the path and every query param (including the live `token`) intact.
 *
 * A generated app only authenticates while embedded — its identity comes from the
 * parent-frame bridge, not the URL — so verifying a foundation update against a
 * preview build means serving that build in a real dashboard iframe.
 *
 * This is the ONLY control protecting the token — the override is honored for client
 * sessions too, because the client surface is where auth regressions surface — so it
 * fails closed on anything it cannot prove is ours. A crafted link naming an
 * arbitrary host would otherwise hand that host a live token. Accepted targets are
 * the app's own production alias, or a deployment of that same Vercel project inside
 * our own team namespace. The team suffix is load-bearing: a bare `<project>-*` rule
 * would also match a `<project>-evil.vercel.app` project a stranger registered.
 */
export const applyPreviewOrigin = (
  embedCode: string,
  previewUrl?: string,
): string => {
  if (!previewUrl) return embedCode;
  try {
    const embed = new URL(embedCode);
    const preview = new URL(previewUrl);
    if (preview.protocol !== 'https:') return embedCode;
    // The embed must itself be a Vercel-hosted app: a custom-domain embed reduces
    // to a generic first label (`app.example.com` -> `app`) that an unrelated
    // `app-*.vercel.app` host would match by accident.
    if (!embed.hostname.endsWith(VERCEL_APP_DOMAIN)) return embedCode;

    // Production aliases come in two shapes — `app-<id>.vercel.app` and the
    // team-suffixed `app-<id>-assembly-apps.vercel.app` — so strip the team
    // suffix before comparing, or a real preview of a team-suffixed app
    // (`app-<id>-<hash>-assembly-apps…`) would be rejected.
    const [embedLabel] = embed.hostname.split('.');
    const project = embedLabel.endsWith(ASSEMBLY_APPS_LABEL_SUFFIX)
      ? embedLabel.slice(0, -ASSEMBLY_APPS_LABEL_SUFFIX.length)
      : embedLabel;
    const isSameProjectDeployment =
      preview.hostname === `${project}${VERCEL_APP_DOMAIN}` ||
      (preview.hostname.startsWith(`${project}-`) &&
        preview.hostname.endsWith(ASSEMBLY_APPS_VERCEL_SUFFIX));
    if (!isSameProjectDeployment) return embedCode;

    return new URL(
      `${embed.pathname}${embed.search}${embed.hash}`,
      preview.origin,
    ).toString();
  } catch (e) {
    return embedCode;
  }
};

/**
 * Host path for an installed app. With `deep-link-app-inner-location` on
 * (`canonical`), the install id and inner page live in the path so the URL can
 * be shared; otherwise it is the legacy `/apps?id=` form, which cannot carry an
 * inner page.
 */
export const getAppPath = (
  installId: string,
  {
    canonical,
    innerPath,
    query = {},
  }: {
    canonical: boolean;
    innerPath?: string;
    query?: Record<string, string>;
  },
): string => {
  if (!canonical) {
    return `${CLIENT_APPS_PAGE.path}?${new URLSearchParams({ id: installId, ...query }).toString()}`;
  }
  const inner =
    innerPath && innerPath !== '/' ? `/${innerPath.replace(/^\/+/, '')}` : '';
  const search = new URLSearchParams(query).toString();
  return `${CLIENT_APPS_PAGE.path}/${installId}${inner}${search ? `?${search}` : ''}`;
};

/**
 * Appends an inner path to an embed URL after its surface prefix.
 * Internal surface (path "/") → "/orders/42"; client surface ("/c") → "/c/orders/42".
 * The surface prefix is the embed URL's existing pathname with trailing slash stripped.
 */
export const applyInnerPath = (
  embedCode: string,
  innerPath: string,
): string => {
  if (!innerPath || innerPath === '/') return embedCode;
  try {
    const url = new URL(embedCode);
    const surfacePrefix = url.pathname.replace(/\/$/, '');
    url.pathname =
      surfacePrefix + (innerPath.startsWith('/') ? innerPath : `/${innerPath}`);
    return url.toString();
  } catch {
    return embedCode;
  }
};

export const applyQueryParams = (
  url: string | URL,
  queryParams: Record<string, string>[],
  currentClient?: ClientType,
  currentCompany?: CompanyType,
  clientCustomFields?: Record<string, CustomFieldOption>,
  companyCustomFields?: Record<string, CustomFieldOption>,
): string => {
  if (!queryParams?.length) {
    return url.toString();
  }

  try {
    const urlObj = new URL(url.toString());

    queryParams.forEach(({ key, value }) => {
      let resolvedValue: string = '';
      const isToken =
        value.startsWith('client.') || value.startsWith('company.');

      if (value.startsWith('client.') && currentClient) {
        const [, clientKey] = value.split('.');
        if (clientKey === 'id') {
          if (currentClient?.[clientKey]) {
            resolvedValue = currentClient?.[clientKey];
          }
        } else if (
          clientKey === 'email' ||
          clientKey === 'givenName' ||
          clientKey === 'familyName'
        ) {
          if (currentClient?.fields?.[clientKey]) {
            resolvedValue = currentClient?.fields?.[clientKey];
          }
        } else if (z.string().uuid().safeParse(clientKey.trim()).success) {
          const rawValue = currentClient?.fields?.customFields?.[clientKey];
          resolvedValue = resolveCustomFieldValue(
            rawValue,
            clientCustomFields,
            clientKey,
          );
        }
      }

      if (value.startsWith('company.') && currentCompany) {
        const [, companyKey] = value.split('.');
        if (currentCompany && companyKey === 'id') {
          resolvedValue = currentCompany?.[companyKey] ?? '';
        } else if (companyKey === 'name') {
          resolvedValue = currentCompany?.fields?.[companyKey] ?? '';
        } else if (z.string().uuid().safeParse(companyKey.trim()).success) {
          const rawValue = currentCompany?.fields?.customFields?.[companyKey];
          resolvedValue = resolveCustomFieldValue(
            rawValue,
            companyCustomFields,
            companyKey,
          );
        }
      }

      if (!isToken) {
        resolvedValue = value;
      }

      if (resolvedValue) {
        urlObj.searchParams.set(key, resolvedValue);
      }
    });

    return urlObj.toString();
  } catch (ex) {
    // If URL parsing fails, return the original URL
    return url.toString();
  }
};

/**
 * Prefixes `https://` onto a bare URL such as `example.com`. People routinely
 * leave the scheme off, and without one the URL can't be parsed for an embed
 * and opens as a path relative to the portal when used as a link. Embed
 * snippets and anything else that isn't a URL pass through unchanged.
 */
export const withDefaultProtocol = (content: string): string => {
  const trimmed = content.trim();
  if (
    UrlUtils.startsWithProtocol(trimmed) ||
    !URLValidationRegex.test(trimmed)
  ) {
    return content;
  }
  return `https://${trimmed}`;
};

const autoSizeElementTypes = ['div', 'iframe'];

/**
 * Checks if a URL string is a localhost URL (http://localhost:port or http://127.0.0.1:port)
 */
const isLocalhostUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return (
      urlObj.hostname === 'localhost' ||
      urlObj.hostname === '[::1]' ||
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(
        urlObj.hostname,
      )
    );
  } catch {
    return false;
  }
};

/**
 * Validates if an embed URL is allowed.
 * - Standard URLs must match URLValidationRegex
 * - Localhost URLs are only allowed when the webapp is running on localhost,
 *   or when explicitly permitted (e.g., dev-mode)
 */
const isValidEmbedUrl = (url: string, allowLocalhost = false): boolean => {
  // Check if it's a localhost URL
  if (isLocalhostUrl(url)) {
    // Allow localhost if explicitly permitted (e.g., dev-mode) or webapp is on localhost
    return allowLocalhost || UrlUtils.isLocalhost();
  }

  // For non-localhost URLs, use the standard validation regex
  const urlRegex = new RegExp(URLValidationRegex);
  return urlRegex.test(url);
};

// allow-same-origin and allow-scripts intentionally coexist: removing allow-same-origin breaks
// localStorage/cookies/fetch on same-site embeds. MDN warns that same-origin iframes served from
// the same origin as the host can call frameElement.removeAttribute('sandbox') to escape — this
// is accepted because Assembly marketplace apps are always hosted on third-party origins in
// production. Do not add allow-same-origin to untrusted, same-origin embeds.
//
// allow-popups-to-escape-sandbox is required alongside allow-popups: without it, a new tab opened
// from inside the embed (e.g. a Google Drive link in the Tasks app) inherits the iframe's sandbox
// and the destination site is blocked ("drive.google.com is blocked"). It lets popups open as a
// normal top-level context instead.
export const APP_EMBED_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-downloads allow-top-navigation-by-user-activation allow-top-navigation-to-custom-protocols';

// Cross-origin iframes only get these features if the host delegates them, so embedded apps need
// camera, microphone and display-capture here to be able to prompt the user for video, audio or screen capture.
// The attribute is a Permissions Policy, so features are separated by semicolons, not spaces.
export const APP_EMBED_ALLOW =
  'clipboard-write; camera; microphone; display-capture';

// "origin" (not "no-referrer") because the @assembly-js/app-bridge SDK inside the embedded app
// derives the host's origin from document.referrer and silently drops every iframe→host message
// (header CTAs, toasts, token-refresh requests) when it is empty. "origin" still withholds the
// dashboard's path/query from third-party apps.
export const APP_EMBED_REFERRER_POLICY = 'origin';

export const createIFrame = (embedURL: string) =>
  `<iframe src="${embedURL}"></iframe>`;

// Embedly-wrapped embeds render an outer wrapper iframe whose src points at
// cdn.embedly.com/widgets/media.html and carries the real embed target,
// URL-encoded, in its `src` query param. Query params set on the wrapper are
// ignored by Embedly and never reach the target, so we must unwrap it and apply
// params to the inner URL instead.
const EMBEDLY_WRAPPER_HOST = 'cdn.embedly.com';
const EMBEDLY_WRAPPER_PATH = '/widgets/media.html';
const EMBEDLY_INNER_URL_PARAM = 'src';

const isAbsoluteHttpUrl = (value: string): boolean => {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Returns the decoded inner target URL of an Embedly wrapper URL, or undefined
 * when the URL is not an Embedly wrapper (or carries no valid inner target).
 * The path match tolerates an optional trailing slash, and the inner target
 * must be an absolute http(s) URL; anything else safely falls through to the
 * plain-URL param path (i.e. the pre-existing behavior).
 */
const getEmbedlyInnerUrl = (url: URL): string | undefined => {
  const isWrapperPath =
    url.pathname === EMBEDLY_WRAPPER_PATH ||
    url.pathname === `${EMBEDLY_WRAPPER_PATH}/`;
  if (url.hostname !== EMBEDLY_WRAPPER_HOST || !isWrapperPath) {
    return undefined;
  }
  const innerUrl = url.searchParams.get(EMBEDLY_INNER_URL_PARAM);
  return innerUrl && isAbsoluteHttpUrl(innerUrl) ? innerUrl : undefined;
};

interface CreateEmbedBodyInput {
  embedCode: string;
  autoSize: boolean;
  customAppParams?: {
    // query params to add to the embed url
    companyId: string;
    clientId?: string; // client id is optional for company channels when viewing as internal user
  };
  queryParams?: Record<string, string>[];
  currentClient?: ClientType;
  currentCompany?: CompanyType;
  clientCustomFields?: Record<string, CustomFieldOption>;
  companyCustomFields?: Record<string, CustomFieldOption>;
  /** Allow localhost URLs even when webapp is not on localhost (e.g., dev-mode) */
  allowLocalhost?: boolean;
}

export const createEmbedBody = ({
  embedCode,
  autoSize,
  customAppParams,
  queryParams,
  currentClient,
  currentCompany,
  clientCustomFields,
  companyCustomFields,
  allowLocalhost,
}: CreateEmbedBodyInput) => {
  let embedSnippet = embedCode;
  if (isValidEmbedUrl(embedCode, allowLocalhost)) {
    try {
      const url = new URL(withDefaultProtocol(embedCode));
      // when customAppParams is provided append the params to the url
      if (customAppParams) {
        Object.entries(customAppParams).forEach(([key, value]) => {
          url.searchParams.set(key, value ?? '');
        });
      }

      // get full url and create Iframe snippet
      embedSnippet = createIFrame(url.toString());
    } catch (ex) {
      return EmbedErrors.errorRenderExtension;
    }
  }

  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(embedSnippet, 'text/html');
  const iframe = htmlDoc.body.querySelector('iframe');
  let queryParamsUrl: InstanceType<typeof URL> | undefined;
  try {
    queryParamsUrl = new URL(iframe?.src ?? '');
  } catch (ex) {
    // nothing
  }

  if (queryParams && queryParamsUrl && iframe) {
    const embedlyInnerUrl = getEmbedlyInnerUrl(queryParamsUrl);
    if (embedlyInnerUrl) {
      // Apply the params to the wrapped target URL and re-encode it back into
      // the wrapper's `src`, so Embedly forwards them to the embedded page.
      const updatedInnerUrl = applyQueryParams(
        embedlyInnerUrl,
        queryParams,
        currentClient,
        currentCompany,
        clientCustomFields,
        companyCustomFields,
      );
      queryParamsUrl.searchParams.set(EMBEDLY_INNER_URL_PARAM, updatedInnerUrl);
      iframe.setAttribute('src', queryParamsUrl.toString());
    } else {
      const updatedUrl = applyQueryParams(
        queryParamsUrl,
        queryParams,
        currentClient,
        currentCompany,
        clientCustomFields,
        companyCustomFields,
      );
      iframe.setAttribute('src', updatedUrl);
    }
  }

  if (iframe) {
    // Assembly enforces APP_EMBED_SANDBOX as a floor policy on all embeds regardless of any
    // sandbox attribute already present in the customer-supplied embed code. This intentionally
    // widens caller-specified sandboxes to ensure consistent platform behaviour (e.g. allow-downloads).
    iframe.setAttribute('sandbox', APP_EMBED_SANDBOX);

    // Merge allow tokens: always include the APP_EMBED_ALLOW tokens; add local-network-access when allowLocalhost
    const allowTokens = new Set(
      APP_EMBED_ALLOW.split(';')
        .map((token) => token.trim())
        .filter(Boolean),
    );
    if (allowLocalhost) {
      allowTokens.add('local-network-access');
    }
    iframe.setAttribute('allow', Array.from(allowTokens).join('; '));

    iframe.setAttribute('referrerpolicy', APP_EMBED_REFERRER_POLICY);
  }

  // override iframes that have `no` scrolling behavior
  // the explicit check for scrolling=no is recommended since this property
  // is deprecated and we dont want to set the value if it does not need to be
  Array.from(htmlDoc.body.children).forEach((elem) => {
    const elemTagName = elem.tagName.toLowerCase();
    if (elemTagName === 'iframe') {
      const iframeScrollingAttributeValue = elem.getAttribute('scrolling');
      if (iframeScrollingAttributeValue === 'no') {
        (elem as HTMLIFrameElement).scrolling = 'auto';
      }
    }
  });

  if (!autoSize) {
    return htmlDoc.body.innerHTML;
  }

  let resizeHeight = '100%';
  const resizeAbleElements = Array.from(htmlDoc.body.children).filter((elem) =>
    autoSizeElementTypes.includes(elem.tagName.toLowerCase()),
  );
  if (resizeAbleElements.length > 1) {
    // If there are more than one resize-able element, we'll set the height to
    // 50%, to show multiple elements on the page
    resizeHeight = '50%';
  }
  Array.from(htmlDoc.body.children).forEach((elem) => {
    const elemTagName = elem.tagName.toLowerCase();
    if (!autoSizeElementTypes.includes(elemTagName)) {
      return;
    }
    //
    if (elemTagName === 'div') {
      (elem as HTMLDivElement).style.height = resizeHeight;
      (elem as HTMLDivElement).style.width = '100%';
    }
    if (elemTagName === 'iframe') {
      (elem as HTMLIFrameElement).height = resizeHeight;
      (elem as HTMLIFrameElement).width = '100%';
    }
  });

  return htmlDoc.body.innerHTML;
};

/**
 * Method to format the created channel using useChannelForm form
 * @param formData { membersType: is the selected channel of company or client, membersId: list of client ids}
 * @returns
 */
export const formatExtensionChannelMembers = (
  formData: InitialConnectAppFormValues,
) => {
  const extensionItemFields: Partial<ExtensionItemFields> = {};
  const { memberIDs, membersType, companyID } = formData.fields;
  if (!memberIDs) return extensionItemFields;

  const clientMemberIDs: Record<string, boolean> = {};
  if (membersType === 'company') {
    // For company channels, we only need the companyID
    extensionItemFields.companyID = companyID;
  } else {
    // For individual and group channels, we need the member IDs
    memberIDs.forEach((clientID) => {
      clientMemberIDs[clientID] = true;
    });
    extensionItemFields.memberIds = clientMemberIDs;
  }
  return extensionItemFields;
};

/**
 * Method to check is the newly created channel already existed or not
 * @param dashboardItems list of all dashboard items i.e. app channels
 * @param item new selected channel that we are checking for
 * @param enableManyCompanies
 * @returns
 */
export const findExistingDashboardItem = (
  dashboardItems: ExtensionItem[],
  item: Partial<ExtensionItem>,
) => {
  const dashboardMembersMap = item.fields?.memberIds || {};
  const newDashboardMemberIds = Object.keys(dashboardMembersMap);
  return dashboardItems.find((extensionChannelItem) => {
    // check extension configId matches
    if (
      extensionChannelItem.fields.extensionConfigId !==
      item.fields?.extensionConfigId
    ) {
      return false;
    }

    // TODO(Neil:EnableManyCompanies) this if is the only code path we should have
    // once we've backfilled all the embeds to have membershipType set. Going forward
    // this value should always be present
    if (extensionChannelItem.membershipType) {
      // if membershipType is set on the channel use this directly to
      // see if there an existing channel with the same membershipType
      // and company/client composition
      const isSameMembershipType =
        extensionChannelItem.membershipType === item.membershipType;
      const isSameCompanyID =
        extensionChannelItem.fields.companyID === item.fields?.companyID;
      if (extensionChannelItem.membershipType === 'company') {
        return isSameMembershipType && isSameCompanyID;
      } else if (isSameCompanyID) {
        const existingDashboardMemberIds = Object.keys(
          extensionChannelItem.fields.memberIds || {},
        );
        return (
          isSameMembershipType &&
          isSameCompanyID &&
          existingDashboardMemberIds.length === newDashboardMemberIds.length &&
          existingDashboardMemberIds.every(
            (dashboardMemberId) => item.fields?.memberIds?.[dashboardMemberId],
          )
        );
      }
    }

    // if enableManyCompanies is true, we need to check if the new extension channel has a companyID matches with an existing channel with companyID we return that existing channel otherwise skip
    const extItemCompanyID = extensionChannelItem.fields.companyID;
    if (extItemCompanyID) {
      // if the new extension channel has a companyID matches with an existing channel with companyID we return that existing channel otherwise skip
      return extItemCompanyID === item.fields?.companyID;
    }
    // if dashboard extension matches item and channelMembers in current
    const existingDashboardMemberIds = Object.keys(
      extensionChannelItem.fields.memberIds || {},
    );
    return (
      // check channel members in extension match item members
      existingDashboardMemberIds.length === newDashboardMemberIds.length &&
      existingDashboardMemberIds.every(
        (dashboardMemberId) => dashboardMembersMap[dashboardMemberId],
      )
    );
  });
};
